import { useQueries } from '@tanstack/react-query';

import { ActivityType, fetchActivitiesForFieldUuids } from 'activities/senders';
import { FIELDS_ACTIVITIES_QUERY_KEY } from './constants';
import { getActiveUserId } from 'current/selectors';
import { useAppSelector } from 'app/hooks';
import type { Activity } from 'store/types/types';
import Dataloader from 'dataloader';

interface UseGetActivitiesForFieldsParams {
  activityType?: ActivityType;
  fieldUuids: string[];
}

interface GetActivitiesQueryOptionsParams {
  userId: string;
  activityType?: ActivityType;
  fieldUuid: string;
}

interface BatchedActivitiesParams {
  activityType: ActivityType;
  fieldUuid: string;
  userId: string;
}

const activitiesLoader = new Dataloader<BatchedActivitiesParams, Activity[]>(
  async (params) => {
    const fieldUuids = params.map(({ fieldUuid }) => fieldUuid);
    const activityType = params[0].activityType;
    const userId = params[0].userId;

    const { result }: { result: Activity[] } = await fetchActivitiesForFieldUuids({
      activityType,
      fieldUuids,
      userId
    });

    const activitiesByField = new Map<string, Activity[]>();

    for (const activity of result) {
      const currentActivities = activitiesByField.get(activity['field-uuid']) || [];
      activitiesByField.set(activity['field-uuid'], [...currentActivities, activity]);
    }

    return params.map(({ fieldUuid }) => activitiesByField.get(fieldUuid) ?? []);
  },
  {
    cache: false
  });

export const getActivitiesQueryOptions = ({ activityType, fieldUuid, userId }: GetActivitiesQueryOptionsParams) => {
  return {
    queryKey: [FIELDS_ACTIVITIES_QUERY_KEY, activityType, fieldUuid],
    queryFn: (): Promise<Activity[]> => activitiesLoader.load({ activityType, userId, fieldUuid }),
    staleTime: Infinity,
    cacheTime: Infinity,
  };
};

export const useGetActivitiesForFields = ({ activityType, fieldUuids }: UseGetActivitiesForFieldsParams) => {
  const userId = useAppSelector(getActiveUserId);

  return useQueries({
    queries: fieldUuids.map((fieldUuid) => getActivitiesQueryOptions({ activityType, fieldUuid, userId }))
  });
};
